import React from 'react';

function PersonalRetirementAccount() {
  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative h-[46vh] min-h-[360px] flex items-center justify-center overflow-hidden">
        <img src="/images/real-people/young-businesswoman-sitting-office_1098-2026.png" alt="Personal Retirement Account" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/60 to-black/80" />
        <div className="relative z-10 text-center px-6">
          <h1 className="text-white text-4xl sm:text-5xl font-semibold tracking-tight">Personal Retirement Account</h1>
          <p className="mt-3 text-white/90 max-w-2xl mx-auto">Tax-advantaged investing designed for dependable income and long-term growth.</p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-start">
          <div data-aos="fade-up" data-aos-delay="100">
            <h2 className="text-3xl font-semibold text-gray-900">A retirement plan built around you</h2>
            <p className="mt-4 text-gray-600">Your Personal Retirement Account (PRA) aligns contributions, risk, and drawdown to your unique time horizon. We combine low-cost core building blocks with active risk controls and periodic rebalancing to keep you on track.</p>
            <ul className="mt-6 space-y-3 text-gray-700">
              <li className="flex gap-2"><span className="mt-2 w-2 h-2 bg-[#0056d7] rounded-full" /> Goals-based allocation with glidepaths</li>
              <li className="flex gap-2"><span className="mt-2 w-2 h-2 bg-[#0056d7] rounded-full" /> Automatic rebalancing and dividend reinvestment</li>
              <li className="flex gap-2"><span className="mt-2 w-2 h-2 bg-[#0056d7] rounded-full" /> Transparent fees and clear reporting</li>
            </ul>
          </div>
          <img data-aos="fade-left" data-aos-delay="150" src="/images/african-man-black-suit_1157-45575.jpg_rbjcix.jpg" alt="Retirement Planning" className="w-full h-80 object-cover rounded-xl shadow" />
        </div>
      </section>

      {/* Investment Options */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div data-aos="fade-up" className="text-center mb-10">
            <h3 className="text-2xl font-semibold text-gray-900">Choose an investment track</h3>
            <p className="text-gray-600 mt-2">Pick a default track and personalize with optional satellite exposures.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { t: 'Target Date', d: 'Age-based glidepaths that steadily reduce equity exposure as you approach retirement.' },
              { t: 'Income Focus', d: 'Dividend equities, investment-grade credit and multi-asset income sleeves.' },
              { t: 'Inflation Guard', d: 'TIPS, commodities and real assets designed to defend purchasing power.' }
            ].map((x) => (
              <div key={x.t} data-aos="fade-up" className="rounded-xl bg-white p-6 shadow border border-gray-100">
                <h4 className="text-lg font-semibold text-gray-900">{x.t}</h4>
                <p className="mt-2 text-gray-600 text-sm">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contributions */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-8">
          <div data-aos="fade-up">
            <h3 className="text-2xl font-semibold text-gray-900">Contributions</h3>
            <p className="mt-3 text-gray-600">Flexible monthly or quarterly funding with employer match support where applicable. We guide you on statutory limits and optimize tax-advantaged contributions.</p>
          </div>
          <div data-aos="fade-up" data-aos-delay="100" className="rounded-xl bg-white p-6 shadow border border-gray-100">
            <h4 className="text-lg font-semibold text-gray-900">Funding methods</h4>
            <ul className="mt-3 space-y-2 text-gray-700 text-sm">
              <li className="flex gap-2"><span className="mt-1 w-2 h-2 bg-[#0056d7] rounded-full" /> Payroll deduction / standing order</li>
              <li className="flex gap-2"><span className="mt-1 w-2 h-2 bg-[#0056d7] rounded-full" /> Bank transfer and card payments</li>
              <li className="flex gap-2"><span className="mt-1 w-2 h-2 bg-[#0056d7] rounded-full" /> Employer match onboarding</li>
            </ul>
          </div>
          <div data-aos="fade-up" data-aos-delay="150" className="rounded-xl bg-white p-6 shadow border border-gray-100">
            <h4 className="text-lg font-semibold text-gray-900">Withdrawals</h4>
            <ul className="mt-3 space-y-2 text-gray-700 text-sm">
              <li className="flex gap-2"><span className="mt-1 w-2 h-2 bg-[#0056d7] rounded-full" /> Regulated early-withdrawal rules</li>
              <li className="flex gap-2"><span className="mt-1 w-2 h-2 bg-[#0056d7] rounded-full" /> Retirement income drawdown options</li>
              <li className="flex gap-2"><span className="mt-1 w-2 h-2 bg-[#0056d7] rounded-full" /> Annuity and lump-sum guidance</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-6xl mx-auto px-6">
          <div data-aos="fade-up" className="text-center mb-12">
            <h3 className="text-2xl font-semibold text-gray-900">A simple 4-step onboarding</h3>
            <p className="text-gray-600 mt-2">Move from intent to invested in days — with guidance at every step.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { n: '01', t: 'Discovery', d: 'We clarify goals, horizon, liquidity needs, and risk tolerance.' },
              { n: '02', t: 'Account Setup', d: 'KYC/AML verification and contribution preferences.' },
              { n: '03', t: 'Portfolio Build', d: 'Select track, apply custom tilts, and set rebalancing cadence.' },
              { n: '04', t: 'Review & Launch', d: 'Confirm fees, disclosures and go live with monitoring.' }
            ].map((s) => (
              <div key={s.n} data-aos="fade-up" className="relative p-6 bg-white rounded-2xl shadow border border-gray-100">
                <div className="absolute -top-4 left-6 w-10 h-10 bg-[#0056d7] text-white rounded-full flex items-center justify-center font-bold">{s.n}</div>
                <h4 className="mt-6 text-lg font-semibold text-gray-900">{s.t}</h4>
                <p className="mt-2 text-gray-600 text-sm">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fees */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-start">
          <div data-aos="fade-right">
            <h3 className="text-2xl font-semibold text-gray-900">Fees & transparency</h3>
            <p className="mt-3 text-gray-600">We keep costs clear and competitive. No hidden fees — ever. You receive itemized statements and can view positions and performance 24/7.</p>
            <ul className="mt-4 space-y-2 text-gray-700 text-sm">
              <li className="flex gap-2"><span className="mt-1 w-2 h-2 bg-[#0056d7] rounded-full" /> Advisory fee based on assets under management</li>
              <li className="flex gap-2"><span className="mt-1 w-2 h-2 bg-[#0056d7] rounded-full" /> Underlying fund expense ratios disclosed</li>
              <li className="flex gap-2"><span className="mt-1 w-2 h-2 bg-[#0056d7] rounded-full" /> No performance fees for standard PRA tracks</li>
            </ul>
          </div>
          <div data-aos="fade-left" className="rounded-2xl bg-gray-50 p-8 border border-gray-100">
            <h4 className="text-lg font-semibold text-gray-900">What you get</h4>
            <div className="mt-4 grid sm:grid-cols-2 gap-4 text-gray-700 text-sm">
              {[
                'Dedicated advisor access',
                'Quarterly performance reviews',
                'Risk and allocation dashboards',
                'Goal tracking and alerts',
              ].map((b) => (
                <div key={b} className="flex gap-2"><span className="mt-1 w-2 h-2 bg-[#0056d7] rounded-full" /> {b}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div data-aos="fade-up" className="text-center mb-10">
            <h3 className="text-2xl font-semibold text-gray-900">Frequently asked questions</h3>
            <p className="text-gray-600 mt-2">Quick answers about eligibility, funding, and withdrawals.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { q: 'Who is eligible for a PRA?', a: 'Adults with verifiable identity and income. Employer-sponsored options may have additional criteria.' },
              { q: 'How much can I contribute?', a: 'We guide you through current statutory contribution limits and how to maximize employer matches.' },
              { q: 'Can I change my track later?', a: 'Yes. You can update allocations during reviews or after qualifying life events.' },
              { q: 'What happens at retirement?', a: 'We transition your portfolio to income-focused allocations and plan orderly withdrawals.' },
            ].map((f) => (
              <div key={f.q} data-aos="fade-up" className="p-6 bg-white rounded-xl shadow border border-gray-100">
                <h4 className="text-lg font-semibold text-gray-900">{f.q}</h4>
                <p className="mt-2 text-gray-600 text-sm">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-semibold">Plan your retirement with confidence</h3>
          <p className="mt-2 text-white/80">Set your contribution schedule, pick a track, and let disciplined processes work for you.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href="/apply" className="px-7 py-3 rounded-full bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] text-black font-semibold shadow-lg hover:opacity-95 transition">Apply now</a>
            <a href="/contact" className="px-7 py-3 rounded-full bg-white/10 hover:bg-white/15 border border-white/20 transition">Talk to an advisor</a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default PersonalRetirementAccount;
